import {
  ChangeDetectorRef,
  Component,
  ComponentRef,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
  ViewContainerRef,
  AfterViewInit,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Order } from '@app/services/order/order.model';
import { OrderService } from '@app/services/order/order.service';
import { PrintService } from '@app/services/print/print.service';
import { ScannedOrderMultipleResults } from '@pages/workspace/fulfill/components/fulfill-side-panel-scan/components/scan-multiple-options/scan-multiple-options';
import { handleScanOrderOrLabel } from '@pages/workspace/fulfill/utils';
import { IShopMetadata } from '@pdqdev/pdq-shared/dist/interfaces/IShopMetadata';
import { ToastService } from '@storybook/services/toast/toast.service';
import { getOrderSplitIndicesString } from '@utils/order-split-utils';
import { Subject } from 'rxjs';
import { first } from 'rxjs/operators';
import {
  PrintBatchOptionFunctionsMap,
  PrintDocumentType,
  buildPrintOptions,
  extractPrintSectionsAndOptions,
} from 'src/utils/print-utils';
import { FulfillService } from '../../fulfill.service';
import { OrderInBulkFulfillmentProcess, PanelState, ScanResponse } from '../../utils/models';
import { ApiService } from '@app/core/services/api/api.service';
import { uniqBy } from 'lodash';

const PrintActions = [
  PrintDocumentType.LABELS,
  PrintDocumentType.PICK_LIST,
  PrintDocumentType.PACKING_SLIPS,
  PrintDocumentType.BROCHURE,
  PrintDocumentType.COMMERCIAL_INVOICE,
  PrintDocumentType.PACK_AND_SHIP,
  PrintDocumentType.GREETING_CARD,
  PrintDocumentType.CARD_ISLE,
];

@Component({
  selector: 'fulfill-side-panel-batch',
  templateUrl: './fulfill-side-panel-batch.component.html',
  styleUrls: ['./fulfill-side-panel-batch.component.scss'],
  standalone: false,
})
export class FulfillSidePanelBatchComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {
  @ViewChild('scannedOrderMultipleResults', { read: ViewContainerRef }) scannedOrderMultipleResults;
  @ViewChild('bannerContainer', { static: true }) bannerContainer!: ElementRef;
  orderMultipleResultsRef: ComponentRef<ScannedOrderMultipleResults>;
  @Input() batch;
  shopMetadata: IShopMetadata;
  destroy$ = new Subject();
  inWarningOrderIds: string[] = [];
  printOptions;
  printSections;
  searchString: string;
  isOnTrial;
  addedOrders = [];
  addedToBatchOrdersNumbers = [];
  isGoToOrderButtonLoading = false;
  bannersHeight = 0;
  activeTab: string = 'all';
  filteredOrders: Order[] = [];

  constructor(
    private fulfillService: FulfillService,
    private router: Router,
    private route: ActivatedRoute,
    private _printService: PrintService,
    private orderService: OrderService,
    private toastService: ToastService,
    private cdRef: ChangeDetectorRef,
    private apiService: ApiService,
  ) {}

  ngOnDestroy() {
    this.fulfillService.activeTab = 'all';
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.batch.currentValue) {
      this.inWarningOrderIds = changes.batch.currentValue.orders
        .filter(
          (o) =>
            o?.updateType?.length > 0 ||
            o?.updateTypeV2?.length > 0 ||
            o?.pendingUpdates?.filter((update) => !update.dismissedBy).length > 0,
        )
        ?.map((o) => o.id);
      this.initAddedToBatchOrders(changes.batch.currentValue.orders);
      this.filterOrdersByTab();
    }
  }

  ngOnInit(): void {
    this.isOnTrial = this.fulfillService.shopIsOnTrialPlan;
    this.shopMetadata = this.fulfillService.shopMetadata;
    buildPrintOptions(this.shopMetadata, PrintActions, 'batch', null, this.apiService, () =>
      this.fulfillService.getCardIsleDevicesCached(),
    ).then((options) => {
      const formatPrintOptions = extractPrintSectionsAndOptions(options);
      this.printOptions = formatPrintOptions.printOptions;
      this.printSections = formatPrintOptions.printSections;
      this.cdRef.detectChanges();
    });
    this.initAddedToBatchOrders(this.batch.orders);
    this.filterOrdersByTab();
  }

  ngAfterViewInit() {
    const observer = new ResizeObserver(() => {
      this.bannersHeight = this.bannerContainer.nativeElement.offsetHeight + 20;
      this.cdRef.detectChanges();
    });

    observer.observe(this.bannerContainer.nativeElement);
  }

  initAddedToBatchOrders(orders) {
    this.addedOrders = orders.filter((order) => order.deliveryTransaction?.isAddedToBatch);
    this.addedToBatchOrdersNumbers = this.addedOrders.map(
      (order) => `${order.orderNumber}${order?.splitted ? '-' + getOrderSplitIndicesString(order, 'view') : ''}`,
    );
  }

  onChangeTab(event) {
    this.activeTab = event;
    this.fulfillService.activeTab = event;
    this.filterOrdersByTab();
  }

  filterOrdersByTab() {
    if (this.activeTab === 'all') {
      this.filteredOrders = this.batch.orders;
    } else {
      this.filteredOrders = this.addedOrders;
    }
  }

  setOrderId(searchString) {
    // scan split order number escape / char
    this.searchString = searchString.replace(/\//g, '-').trim();
  }

  async scanOrder() {
    if (!this.searchString) {
      return;
    }

    this.router.navigate(['.'], { relativeTo: this.route, queryParams: { from: 'batch' } });
    this.isGoToOrderButtonLoading = true;

    const scanResponse: ScanResponse = handleScanOrderOrLabel(this.searchString, this.batch.orders);

    if (scanResponse.status === 'failed') {
      this.toastService.showToast({ message: scanResponse.message, mode: 'danger' });
      this.isGoToOrderButtonLoading = false;
      if (this.orderMultipleResultsRef) {
        this.orderMultipleResultsRef.destroy();
        this.orderMultipleResultsRef = null;
      }
      return;
    }

    if (scanResponse.status === 'succeeded') {
      if (scanResponse.orders?.length == 1) {
        this.getTargetOrderInfo(scanResponse.orders[0]);
        return;
      }

      // more than one option
      return this.createMultipleOrderSelectionComponent(scanResponse.orders);
    }

    this.isGoToOrderButtonLoading = false;
  }

  createMultipleOrderSelectionComponent(orders: Order[]) {
    if (!this.orderMultipleResultsRef) {
      const uniqueOrders = uniqBy(orders, 'id');
      orders = uniqueOrders.map((o) => ({ ...o, batchName: this.batch.name }));
      this.orderMultipleResultsRef = this.scannedOrderMultipleResults.createComponent(ScannedOrderMultipleResults);
      this.orderMultipleResultsRef.instance.orders = orders;
      this.orderMultipleResultsRef.instance.onClose.pipe(first()).subscribe(() => {
        this.orderMultipleResultsRef.destroy();
        this.orderMultipleResultsRef = null;
      });
      this.isGoToOrderButtonLoading = false;

      this.orderMultipleResultsRef.instance.onSelect.pipe(first()).subscribe((order) => {
        this.isGoToOrderButtonLoading = true;
        this.getTargetOrderInfo(order);
      });
    }
  }

  getTargetOrderInfo(order) {
    this.orderService
      .getOrderObs(order?.id)
      .pipe(first())
      .subscribe(
        (order) => {
          this.fulfillService.setActiveOrder(order);
          this.fulfillService.setPanelState(PanelState.ORDER);
          this.isGoToOrderButtonLoading = true;
        },
        (error) => this.toastService.showToast({ message: 'Cannot find this order', mode: 'danger' }),
      );
  }

  getPrintFormatted(timestamp) {
    return new Date(timestamp).toLocaleTimeString();
  }

  getNumberOfItems() {
    return this.batch.orders.map((o) => o.itemCount ?? 0).reduce((a, b) => a + b, 0);
  }

  print(event, batchId) {
    this.onBatchPrintTriggerSetBulkFulfillmentData(event.action);
    this._printService.printBatch(event.action, { batchId, documentName: event.action });
  }

  onBatchPrintTriggerSetBulkFulfillmentData(action) {
    const metadata = this.fulfillService.shopMetadata;
    if (metadata?.onPrintTriggerFulfillment && action === PrintBatchOptionFunctionsMap['labels']) {
      this.fulfillService.setBulkBatchFulfillmentProcessData({
        batchId: this.batch.id,
        orders: this.batch.orders?.map((order) => ({
          id: order.id,
          fulfillmentProcess: 'running',
        })) as OrderInBulkFulfillmentProcess[],
      });
    }
  }

  closePanel() {
    this.fulfillService.togglePanel(false);
  }

  pickPlan() {
    this.router.navigate(['./settings/billing']);
  }

  getTrialBannerText() {
    return [`Orders can't be fulfilled in trial mode`, `Pick a plan to get labels and fulfill orders`];
  }
}
